import { initiateGam } from '@client/adManager/initiateGam.js';
import { defineAdvtComponent } from '@client/core/components/webComponents/index.js';
import { sendErrorMessageMetricToServer } from '@client/core/services/metrics.js';
import {
  sendAmplitudeExperimentToGam,
  setupAmplitudeExperimentSubscription
} from '@client/core/utils/amplitude/index.js';
import { initiateHandleStickySkyscrapers } from '@client/core/utils/dom/handleStickySkyscrapers.js';
import { WallpaperHandler } from '@client/core/utils/dom/WallpaperHandler.js';
import { setupEvents } from '@client/core/utils/events/index.js';
import {
  persistPPID,
  setPulsePageViewId
} from '@client/core/utils/pulse/pulse.js';
import {
  debug,
  DeviceType,
  type InitialState
} from '@schibsted-nmp/advertising-shared';

import { loadGlimrIfPermission } from './core/utils/glimr.js';
import './dev/AdvtDevTool';

/**
 * Initiates ad rendering, setup page options and handles each placement.
 * @param initialState The initial state for setting up ads.
 */
export async function initiateAdvt(
  initialState: InitialState,
  ...abortSignals: ReadonlyArray<AbortSignal>
): Promise<void> {
  try {
    debug.message('Initializing', { initialState }).info((log) => log());

    const { brand, deviceType, adServer } = initialState.config;

    await Promise.all([
      persistPPID(),
      setPulsePageViewId(),
      loadGlimrIfPermission(brand)
    ]);

    setupEvents();

    if (deviceType === DeviceType.Desktop) {
      initiateHandleStickySkyscrapers();
    }

    WallpaperHandler.initiate();
    defineAdvtComponent();

    // Send Amplitude experiment values to GAM and set up subscription for updates
    sendAmplitudeExperimentToGam();
    setupAmplitudeExperimentSubscription();

    if (adServer.gam) {
      await initiateGam(adServer.gam, ...abortSignals);
    }

    debug.message('Completed initialization').success();
  } catch (error) {
    debug.message('Failed initialization!', error).error();

    if (error instanceof Error) {
      try {
        await sendErrorMessageMetricToServer(
          `Failed to initiate ads ${error.message}`
        );

        debug.message('Sent error message metric to server').info();
      } catch (error: unknown) {
        debug
          .message('Failed sending error message metric to server', error)
          .error();
      }
    }
  }
}
